import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, ArrowsUpDownIcon } from '@heroicons/react/24/outline';

const ProductSort = ({ sortBy, sortOrder, onSortChange, fullWidth = false }) => {
  const [isOpen, setIsOpen] = useState(false);

  const sortOptions = [
    { label: 'Newest First', value: { sortBy: 'createdAt', sortOrder: 'desc' } },
    { label: 'Oldest First', value: { sortBy: 'createdAt', sortOrder: 'asc' } },
    { label: 'Price: Low to High', value: { sortBy: 'price', sortOrder: 'asc' } },
    { label: 'Price: High to Low', value: { sortBy: 'price', sortOrder: 'desc' } },
    { label: 'Name: A to Z', value: { sortBy: 'name', sortOrder: 'asc' } },
    { label: 'Name: Z to A', value: { sortBy: 'name', sortOrder: 'desc' } },
  ];

  const getCurrentSortLabel = () => {
    const currentOption = sortOptions.find(
      (option) => option.value.sortBy === sortBy && option.value.sortOrder === sortOrder
    );
    return currentOption ? currentOption.label : 'Sort by';
  };

  const handleSortChange = (newSortBy, newSortOrder) => {
    onSortChange(newSortBy, newSortOrder);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:border-primary-300 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-primary-500/30 transition-all duration-200 min-w-0 ${
          fullWidth ? 'w-full justify-between' : ''
        }`}
      >
        <ArrowsUpDownIcon className="w-4 h-4 text-slate-400 shrink-0" />
        <span className="text-sm text-slate-600 hidden sm:inline">Sort:</span>
        <span className="text-sm font-medium text-slate-900 truncate max-w-[120px] sm:max-w-none">
          {getCurrentSortLabel()}
        </span>
        <ChevronDownIcon
          className={`w-4 h-4 text-slate-400 shrink-0 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className={`absolute top-full left-0 mt-2 bg-white rounded-xl shadow-large border border-slate-200 z-50 overflow-hidden ${
              fullWidth ? 'w-full' : 'w-56 sm:w-64'
            }`}
          >
            <div className="py-1.5">
              {sortOptions.map((option, index) => {
                const isActive =
                  sortBy === option.value.sortBy && sortOrder === option.value.sortOrder;
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleSortChange(option.value.sortBy, option.value.sortOrder)}
                    className={`w-full px-4 py-2.5 text-left text-sm transition-colors duration-150 ${
                      isActive
                        ? 'bg-primary-50 text-primary-700 font-semibold'
                        : 'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {isOpen && (
        <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} aria-hidden="true" />
      )}
    </div>
  );
};

export default ProductSort;
